/**
 * Mode voyage (R18, G22 — reference : 1Password Travel Mode, Enpass).
 *
 * Principe : les dossiers marques « surs en voyage » restent sur l'appareil,
 * TOUT LE RESTE en est physiquement retire. Pas masque : retire. Un ecran
 * verrouille se deverrouille sous contrainte, une base locale se copie ; la
 * seule protection qui tienne a une frontiere est que la donnee NE SOIT PLUS
 * LA. L'appareil ne peut pas revenir en arriere seul : la desactivation se
 * fait depuis un AUTRE appareil (drapeau porte par le compte serveur,
 * `apps/server/src/travel-mode.ts`), puis la synchro rapatrie le coffre.
 *
 * Ce module ne fait QUE decider. Il ne lit ni n'ecrit rien : `vault-store.ts`
 * (`purgeForTravelMode`) est le chemin qui AGIT, et c'est lui que les tests
 * exercent.
 *
 * ⚠️ Invariant vital : on ne purge QUE ce que le serveur detient deja a une
 * revision au moins aussi recente. Un mode voyage qui perd la seule copie
 * d'une entree n'est pas une protection, c'est une destruction.
 */
import { generateId } from '@klavox/vault-model';

/** Ce que `ensureDeviceId` a besoin de savoir d'un depot local. */
interface DeviceIdStore {
  loadDeviceId(): Promise<string | null>;
  saveDeviceId(deviceId: string): Promise<void>;
}

/**
 * Identifiant de CET appareil, cree au premier besoin et garde tel quel
 * ensuite : c'est lui que le serveur retient pour refuser a l'appareil parti
 * en voyage de se liberer lui-meme. Aleatoire et sans signification -- il ne
 * dit rien de l'appareil, et ne quitte jamais le couple appareil/compte.
 */
export async function ensureDeviceId(repository: DeviceIdStore): Promise<string> {
  const existing = await repository.loadDeviceId();
  if (existing !== null && existing.length > 0) return existing;
  const deviceId = generateId();
  await repository.saveDeviceId(deviceId);
  return deviceId;
}

/** Metadonnees suffisantes pour decider : aucun secret n'entre ici. */
export interface TravelRecordRef {
  readonly id: string;
  readonly revision: number;
}

export interface TravelItemRef extends TravelRecordRef {
  readonly folderId: string | null;
}

export interface TravelAttachmentRef extends TravelRecordRef {
  readonly itemId: string;
  /** Tombstone : ne porte plus aucun contenu, donc rien a sauvegarder avant de partir. */
  readonly deletedAt: number | null;
}

export interface TravelPurgePlan {
  readonly itemIds: readonly string[];
  readonly folderIds: readonly string[];
  readonly attachmentIds: readonly string[];
  /**
   * Enregistrements qui auraient du partir mais que le serveur ne detient pas
   * encore a jour : ils RESTENT sur l'appareil. L'appelant doit le dire a
   * l'utilisateur -- sinon il croit voyager leger alors que non.
   */
  readonly unsynced: number;
}

/** Dossier dechiffre, reduit a ce dont la decision a besoin. */
export interface TravelFolderRef extends TravelRecordRef {
  readonly parentId: string | null;
  readonly travelSafe: boolean;
}

/**
 * Dossiers surs en voyage, heritage compris : un sous-dossier d'un dossier
 * marque est sur lui aussi. Sans cela, marquer « Voyage » laisserait
 * « Voyage / Hotels » disparaitre de l'appareil, ce que personne n'attend.
 * Les cycles (arbre corrompu) ne bouclent pas : chaque dossier est visite une fois.
 */
export function resolveTravelSafeFolders(folders: readonly TravelFolderRef[]): ReadonlySet<string> {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const safe = new Set<string>();
  for (const folder of folders) {
    const seen = new Set<string>();
    let current: TravelFolderRef | undefined = folder;
    while (current !== undefined && !seen.has(current.id)) {
      seen.add(current.id);
      if (current.travelSafe) {
        safe.add(folder.id);
        break;
      }
      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
  }
  return safe;
}

export interface PlanTravelPurgeInput {
  readonly items: readonly TravelItemRef[];
  readonly folders: readonly TravelFolderRef[];
  readonly attachments: readonly TravelAttachmentRef[];
  /**
   * `id -> revision` que le SERVEUR detient, etabli a partir d'enregistrements
   * deja authentifies sous la `vaultKey` (`authenticateVaultRecords`). Un
   * serveur qui pretend detenir ce qu'il n'a pas ne doit jamais pouvoir
   * provoquer une purge.
   */
  readonly serverRevisions: ReadonlyMap<string, number>;
}

/**
 * Ce qui quitte l'appareil quand le mode voyage s'active.
 *
 * - une entree part si son dossier n'est pas sur en voyage (racine = jamais sure) ;
 * - une entree ne part QUE si le serveur la detient a jour, pieces jointes
 *   ACTIVES comprises -- une piece jointe supprimee (tombstone) ne porte plus
 *   de contenu : exiger qu'elle soit sauvegardee retiendrait son entree pour
 *   toujours, puisque la synchro ne pousse jamais un tombstone que le serveur
 *   n'a jamais connu (`planRecordTransfer`) ;
 * - un dossier ne part que s'il est vide de tout ce qui reste.
 */
export function planTravelPurge(input: PlanTravelPurgeInput): TravelPurgePlan {
  const safeFolders = resolveTravelSafeFolders(input.folders);
  const held = (record: TravelRecordRef): boolean => (input.serverRevisions.get(record.id) ?? -1) >= record.revision;
  const attachmentsByItem = new Map<string, TravelAttachmentRef[]>();
  for (const attachment of input.attachments) {
    const list = attachmentsByItem.get(attachment.itemId);
    if (list === undefined) attachmentsByItem.set(attachment.itemId, [attachment]);
    else list.push(attachment);
  }

  const itemIds: string[] = [];
  const attachmentIds: string[] = [];
  const keptItemFolders = new Set<string | null>();
  let unsynced = 0;
  for (const item of input.items) {
    const safe = item.folderId !== null && safeFolders.has(item.folderId);
    const own = attachmentsByItem.get(item.id) ?? [];
    if (safe || !held(item) || !own.every((attachment) => attachment.deletedAt !== null || held(attachment))) {
      if (!safe) unsynced += 1;
      keptItemFolders.add(item.folderId);
      continue;
    }
    itemIds.push(item.id);
    for (const attachment of own) attachmentIds.push(attachment.id);
  }

  // Un dossier reste s'il est sur, s'il porte encore une entree restee, ou
  // s'il est l'ANCETRE d'un tel dossier : sinon un « Perso / Voyage » garde
  // pointerait vers un parent disparu.
  const byId = new Map(input.folders.map((folder) => [folder.id, folder]));
  const required = new Set<string>();
  for (const folder of input.folders) {
    if (!safeFolders.has(folder.id) && !keptItemFolders.has(folder.id)) continue;
    let current: TravelFolderRef | undefined = folder;
    while (current !== undefined && !required.has(current.id)) {
      required.add(current.id);
      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
  }

  const folderIds: string[] = [];
  for (const folder of input.folders) {
    if (required.has(folder.id)) continue;
    if (!held(folder)) {
      unsynced += 1;
      continue;
    }
    folderIds.push(folder.id);
  }
  return { itemIds, folderIds, attachmentIds, unsynced };
}
